import type { Step } from 'react-joyride';

export interface TourStep extends Step {
  page: string;
}

export interface DemoIds {
  memberId?: string;
  taskId?: string;
  reasonId?: string;
}

/** Static list-page steps (always included) */
const listSteps: TourStep[] = [
  // Dashboard (steps 0-6)
  {
    target: '[data-tour="sidebar-nav"]',
    title: 'Welcome to Flock Care',
    content:
      'This is your navigation sidebar. It gives you quick access to every area of the app.',
    placement: 'right',
    skipBeacon: true,
    page: '/dashboard',
  },
  {
    target: '[data-tour="sidebar-church-name"]',
    title: 'Your Church',
    content: 'This shows your church name and your account info.',
    placement: 'right',
    skipBeacon: true,
    page: '/dashboard',
  },
  {
    target: '[data-tour="dashboard-create-btn"]',
    title: 'Quick Create',
    content:
      'Use this button to quickly create new members, tasks, or care reasons from anywhere.',
    placement: 'bottom',
    skipBeacon: true,
    page: '/dashboard',
  },
  {
    target: '[data-tour="dashboard-my-tasks"]',
    title: 'My Tasks',
    content:
      'See all tasks assigned to you at a glance. Stay on top of your care responsibilities.',
    placement: 'right',
    skipBeacon: true,
    page: '/dashboard',
  },
  {
    target: '[data-tour="dashboard-feed"]',
    title: 'Activity Feed',
    content:
      'The feed shows recent activity across your church — new tasks, care reasons, and updates from your team.',
    placement: 'left',
    skipBeacon: true,
    page: '/dashboard',
  },
  {
    target: '[data-tour="dashboard-birthdays"]',
    title: 'Upcoming Birthdays',
    content:
      'Never miss a birthday. This section shows upcoming birthdays for your church members.',
    placement: 'right',
    skipBeacon: true,
    page: '/dashboard',
  },
  {
    target: '[data-tour="sidebar-members"]',
    title: 'Church Members',
    content:
      "Let's look at your members list. Click Next to see how you manage your congregation.",
    placement: 'right',
    skipBeacon: true,
    page: '/dashboard',
  },

  // Members (steps 7-9)
  {
    target: '[data-tour="members-create-btn"]',
    title: 'Add Members',
    content: 'Use this button to add new church members to your Flock.',
    placement: 'bottom',
    skipBeacon: true,
    page: '/members',
  },
  {
    target: '[data-tour="members-search"]',
    title: 'Search Members',
    content: 'Quickly find any member by name, email, or phone.',
    placement: 'bottom',
    skipBeacon: true,
    page: '/members',
  },
  {
    target: '[data-tour="members-table"]',
    title: 'Member Directory',
    content:
      'View and manage all your church members here. Click any row to see their full profile.',
    placement: 'top',
    skipBeacon: true,
    page: '/members',
  },

  // Tasks transition + steps (10-13)
  {
    target: '[data-tour="sidebar-tasks"]',
    title: 'Tasks',
    content:
      "Now let's check out the task management system. Click Next to continue.",
    placement: 'right',
    skipBeacon: true,
    page: '/members',
  },
  {
    target: '[data-tour="tasks-create-btn"]',
    title: 'Create Tasks',
    content:
      'Create follow-up tasks for your care team — visits, phone calls, check-ins, and more.',
    placement: 'bottom',
    skipBeacon: true,
    page: '/tasks',
  },
  {
    target: '[data-tour="tasks-filter-bar"]',
    title: 'Filter & Organize',
    content:
      'Filter tasks by status, assignee, date, group, and more to find exactly what you need.',
    placement: 'bottom',
    skipBeacon: true,
    page: '/tasks',
  },
  {
    target: '[data-tour="tasks-table"]',
    title: 'Task List',
    content:
      'Your tasks are organized by due date. Each row shows the status, member, assignee, and interaction type.',
    placement: 'top',
    skipBeacon: true,
    page: '/tasks',
  },

  // Care Reasons transition + steps (14-16)
  {
    target: '[data-tour="sidebar-care-reasons"]',
    title: 'Care Reasons',
    content:
      "Finally, let's look at care reasons — the heart of pastoral care tracking.",
    placement: 'right',
    skipBeacon: true,
    page: '/tasks',
  },
  {
    target: '[data-tour="care-reasons-create-btn"]',
    title: 'Log Care Reasons',
    content:
      'Document care needs like hospital visits, grief, illness, or other pastoral concerns.',
    placement: 'bottom',
    skipBeacon: true,
    page: '/care-reasons',
  },
  {
    target: '[data-tour="care-reasons-table"]',
    title: 'Care Reason Records',
    content:
      'View all care reasons here. Sensitive records are marked with a special indicator to protect privacy.',
    placement: 'top',
    skipBeacon: true,
    page: '/care-reasons',
  },
];

/** Detail-page steps (only included when demo data IDs are available) */
function buildDetailSteps(ids: DemoIds): TourStep[] {
  const steps: TourStep[] = [];

  if (ids.memberId) {
    const memberPage = `/members/${ids.memberId}`;
    steps.push(
      {
        target: '[data-tour="member-detail-card"]',
        title: 'Member Profile',
        content:
          "Here's a member's full profile — name, contact info, birthday, and more. Everything you need at a glance.",
        placement: 'bottom',
        skipBeacon: true,
        page: memberPage,
      },
      {
        target: '[data-tour="member-related-tasks"]',
        title: 'Connected Records',
        content:
          'Every member shows their related tasks and care reasons, so you can see the full picture of their pastoral care.',
        placement: 'top',
        skipBeacon: true,
        page: memberPage,
      },
    );
  }

  if (ids.taskId) {
    const taskPage = `/tasks/${ids.taskId}`;
    steps.push(
      {
        target: '[data-tour="task-detail-card"]',
        title: 'Task Details',
        content:
          "Each task shows who it's assigned to, the due date, interaction type, and linked care reason — everything your team needs.",
        placement: 'bottom',
        skipBeacon: true,
        page: taskPage,
      },
      {
        target: '[data-tour="task-notes-card"]',
        title: 'Task Notes',
        content:
          'Add notes to keep a record of conversations, updates, and follow-ups. Your whole team stays in the loop.',
        placement: 'top',
        skipBeacon: true,
        page: taskPage,
      },
    );
  }

  if (ids.reasonId) {
    const reasonPage = `/care-reasons/${ids.reasonId}`;
    steps.push(
      {
        target: '[data-tour="reason-detail-card"]',
        title: 'Care Reason Details',
        content:
          'Each care reason captures the full context — who needs care, what happened, when, and who is responsible.',
        placement: 'bottom',
        skipBeacon: true,
        page: reasonPage,
      },
      {
        target: '[data-tour="reason-notes-card"]',
        title: 'Notes & Follow-up',
        content:
          'Document every pastoral interaction. Notes help your whole care team stay informed and coordinated.',
        placement: 'top',
        skipBeacon: true,
        page: reasonPage,
      },
    );
  }

  return steps;
}

export function buildTourSteps(ids: DemoIds): TourStep[] {
  return [...listSteps, ...buildDetailSteps(ids)];
}

/** Fallback for when IDs haven't loaded yet */
export const tourSteps = listSteps;
